import {hash} from "@/edinet/libs/utils"
import {DocFileType, DocIndex, DocSrc, DocType, Meta, Period} from "@/edinet/types"
import {isVerySmallScreen} from "@/set/search/ScreenUtils";
import {matchKnownDoc} from "@/set/search/SetNewsParser";
import {SetFilingMeta, SetMeta, SetNews} from "../types"
import dayjs from "dayjs";

function getDatePeriodEnd(period: Period, year: number): Date {
    switch (period) {
        case Period.Q1:
            return new Date(year, 3 - 1, 31) //js month starts from 0 - confusing :(
        case Period.Q2:
            return new Date(year, 6 - 1, 30)
        case Period.Q3:
            return new Date(year, 9 - 1, 30)
        case Period.FY:
            return new Date(year, 12 - 1, 31)
        default:
            throw new Error('unknown period: ' + period)
    }
}

function createSetMeta(news: SetNews): SetMeta | SetFilingMeta {
    const datePublish = dayjs(news.date).toDate()
    const title = news.title
    const baseMeta: SetMeta = {
        docType: DocType.Others,
        kind: 'SetMeta',
        //meta
        sym: news.sym,
        datePublish: datePublish,
        //setMeta
        title: title,
        linkText: news.link.text,
    }
    try {
        const knownDoc = matchKnownDoc(title, news)
        if (knownDoc) {
            return {
                ...baseMeta,
                kind: 'SetFilingMeta',
                setDocType: knownDoc.setDocType,
                dateFYEnd: getDatePeriodEnd(Period.FY, knownDoc.year),
                datePeriodEnd: getDatePeriodEnd(knownDoc.period, knownDoc.year),
                period: knownDoc.period,
                year: knownDoc.year
            }
        }
    } catch
        (e) {
        console.warn('error while parsing additional meta. title: %o, error: %o', title, e)
    }

    return baseMeta
}

export function createIndex(news: SetNews): DocIndex {
    const d = new Date()

    const id = hash(news.link.href)
    const m = news.link.href.match(/newsId=(\d+)/)
    let srcId
    if (m && m[1]) {
        srcId = m[1]
    } else {
        console.warn('cannot extract newsId from link %o. use href as srcId', news.link.href)
        srcId = news.link.href
    }
    const meta = createSetMeta(news)
    return {
        id: id,
        type: (meta.kind === 'SetFilingMeta') ? DocType.Financials : DocType.Others,
        fileType: DocFileType.Html,
        date: {
            add: d,
            access: d
        },
        src: DocSrc.SET,
        srcId: srcId,
        srcLocation: news.link.href,
        meta: meta,
        lang: 'en'
    }
}

export function loc(url: string): string {
    if (!url.startsWith('http')) {
        return 'https://classic.set.or.th/' + url
    } else {
        return url
    }
}

export function kind(meta: Meta): string {
    if ((meta as (SetMeta | SetFilingMeta)).kind === 'SetFilingMeta') {
        return 'SetFilingMeta'
    } else {
        return 'SetMeta'
    }
}

function getFileExt(href: string): string {
    const x = href.toLowerCase()
    const i = x.lastIndexOf('.')
    const ext = x.substr(i + 1, x.length - i - 1)
    return ext
}

export function getFileNameFromUrl(href: string): string {
    const extLength = getFileExt(href).length
    const i = href.lastIndexOf('/')
    return href.substr(i + 1, href.length - i - 2 - extLength)
}

const SUPPORTED_VIEWER_EXTENSIONS = ['xls', 'xlsx', 'doc', 'docx']

export function getViewerUrl(href: string, engine: string, useGoogleDriveViewer = false): string {
    if (SUPPORTED_VIEWER_EXTENSIONS.includes(getFileExt(href))) {
        if (engine === 'MS') {
            return 'https://view.officeapps.live.com/op/embed.aspx?src=' + href
        } else {
            return 'http://docs.google.com/gview?embedded=true&url=' + href
        }
    } else {
        if (useGoogleDriveViewer) {
            //to see inline pdf in mobile (instead of downloading pdf)
            return 'https://drive.google.com/viewerng/viewer?embedded=true&url=' + href
        } else {
            return href
        }
    }
}